import React from 'react';
import { AlertCircle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getRiskScoreColor, getTriggerLabel } from '../../constants/riskBand';
import { useI18n } from '../../i18n/I18nContext';
import InlineEmpty from '../InlineEmpty/InlineEmpty';
import { formatDate, formatShortDate } from '../../utils/datetime';
import {
  CHART_MARGIN, CURSOR_PROPS, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, lineProps,
} from '../charts/chartTheme';
import './RiskScoreTrendChart.css';

/**
 * Story 19-3: Risk Score Trend Chart
 *
 * Renders a line chart of historical risk scores for a student.
 * Used inside the "Assessment and Recommendations" tab on the student detail page.
 *
 * NOTE: This component is mentor-only. The parent page (MentorStudentDetail) is
 * gated to TEACHER | MENTOR | ADMIN roles. If this component is ever reused
 * on a student-facing surface the narrativeForMentor must NOT be passed through
 * and the parent gate must be TEACHER | MENTOR | ADMIN.
 *
 * Drawn with the shared chart theme (components/charts/chartTheme): dashed horizontal grid only,
 * no axis lines or ticks, 12px muted tick text, the one themed tooltip look. The line is the
 * series' yellow; each dot carries the risk band colour of its score.
 *
 * @param {{ history: HistoryPoint[], loading: boolean, error: string|null }} props
 *
 * HistoryPoint: { id, riskScore, createdAt, trigger }
 */

/**
 * Translate a raw trigger value via risk.trigger.<value>, falling back to
 * the framework-agnostic getTriggerLabel() title-casing for unmapped values.
 */
function triggerLabel(t, trigger) {
  const key = trigger ? `risk.trigger.${trigger}` : 'risk.trigger.unknown';
  const val = t(key);
  return val !== key ? val : getTriggerLabel(trigger);
}

function RiskScoreTrendChart({ history = [], loading = false, error = null }) {
  const { t } = useI18n();

  if (loading) {
    // A block the size of the chart, so nothing jumps when the line arrives
    return (
      <div className="rstc-container" aria-live="polite" aria-busy="true">
        <div className="ui-skel rstc-skel" aria-hidden="true" />
        <span className="visually-hidden">{t('ui.loadingTrendData')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui-alert rstc-error" role="alert">
        <AlertCircle aria-hidden="true" />
        <p>{error}</p>
      </div>
    );
  }

  if (!history || history.length < 2) {
    return <InlineEmpty kind="empty" title={t('ui.notEnoughTrendData')} />;
  }

  const chartData = history.map((point) => ({
    ...point,
    dateLabel: formatShortDate(point.createdAt),
    triggerLabel: triggerLabel(t, point.trigger),
  }));

  return (
    <div className="rstc-container">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={CHART_MARGIN}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="dateLabel" {...X_AXIS_PROPS} />
          <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} {...Y_AXIS_PROPS} width={32} />
          <Tooltip content={<RiskTooltip />} cursor={CURSOR_PROPS} />
          <Line
            dataKey="riskScore"
            {...lineProps(0)}
            dot={<ColoredDot />}
            activeDot={<ColoredDot active />}
            name={t('ui.riskScoreLabel')}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom dot — color-coded to risk band at that score
// ---------------------------------------------------------------------------

function ColoredDot(props) {
  const { cx, cy, payload, active } = props;
  if (cx == null || cy == null) return null;
  const color = getRiskScoreColor(payload?.riskScore);
  const r = active ? 6 : 4;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={color}
      stroke="var(--color-surface)"
      strokeWidth={1.5}
    />
  );
}

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------

function RiskTooltip({ active, payload }) {
  const { t } = useI18n();
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="ui-chart-tooltip rstc-tooltip" role="status">
      <p className="ui-chart-tooltip__label">{formatDate(point.createdAt)}</p>
      <div className="ui-chart-tooltip__row">
        <span className="ui-chart-tooltip__dot" style={{ '--ui-dot': getRiskScoreColor(point.riskScore) }} />
        <span className="ui-chart-tooltip__name">{t('ui.riskScoreLabel')}</span>
        <span>{point.riskScore}/10</span>
      </div>
      <p className="rstc-tooltip-trigger">{t('ui.triggerLabel')} {point.triggerLabel}</p>
    </div>
  );
}

export default RiskScoreTrendChart;
